import { completeHexColor } from '@/lib/color/hexColorInput';

const CONTRAST_CANDIDATES = ['#ffffff', '#000000', '#f5f5f5', '#111111'] as const;

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = completeHexColor(hex);
  if (!normalized) return null;
  const value = normalized.slice(1);
  const channels = value.length === 3
    ? value.split('').map((char) => parseInt(char + char, 16))
    : [value.slice(0, 2), value.slice(2, 4), value.slice(4, 6)].map((part) => parseInt(part, 16));
  if (channels.some((channel) => Number.isNaN(channel))) return null;
  return { r: channels[0]!, g: channels[1]!, b: channels[2]! };
}

function channelLuminance(channel: number): number {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return (
    0.2126 * channelLuminance(rgb.r)
    + 0.7152 * channelLuminance(rgb.g)
    + 0.0722 * channelLuminance(rgb.b)
  );
}

export function contrastRatio(hexA: string, hexB: string): number {
  const lumA = relativeLuminance(hexA);
  const lumB = relativeLuminance(hexB);
  if (lumA == null || lumB == null) return 1;
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Deterministic pick of the highest-contrast light/dark candidate. */
export function pickOppositeColor(accentHex: string): string {
  const ranked = [...CONTRAST_CANDIDATES]
    .map((candidate) => ({ candidate, ratio: contrastRatio(accentHex, candidate) }))
    .sort((left, right) => {
      if (right.ratio !== left.ratio) return right.ratio - left.ratio;
      return left.candidate.localeCompare(right.candidate);
    });

  return ranked[0]?.candidate ?? '#000000';
}

/** Picks a light or dark text color with strong contrast against the accent. */
export function pickContrastingColor(accentHex: string, random: () => number = Math.random): string {
  const ranked = [...CONTRAST_CANDIDATES]
    .map((candidate) => ({ candidate, ratio: contrastRatio(accentHex, candidate) }))
    .sort((left, right) => {
      if (right.ratio !== left.ratio) return right.ratio - left.ratio;
      return left.candidate.localeCompare(right.candidate);
    });

  const bestRatio = ranked[0]?.ratio ?? 1;
  const tied = ranked.filter((entry) => entry.ratio === bestRatio);
  const index = Math.floor(random() * tied.length);
  return tied[index]?.candidate ?? '#ffffff';
}
